import React, { useCallback } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import ReportTable from "components/reports/shared/ReportTable";
import { formatPercent as formatPercentValue } from "components/reports/shared/reportFormatters";
import { getChartColors } from "theme";
import api from "services/api";

const STATUS_COLORS = {
  Accepted: "success.main",
  "Not available": "warning.main",
  "No response": "info.main",
  "Will confirm back": "primary.main",
  "Not reached": "error.main",
  Declined: "error.main",
  Other: "text.secondary",
};

const formatNumber = (value) => (Number(value) || 0).toLocaleString();

/**
 * Outreach & Fill Rate
 *
 * Measures the pre-assignment workflow: how many linguists were contacted for
 * services in the period, how they answered, and how often the service ended
 * up staffed.
 */
export const OutreachFillRate = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/linguist/outreach", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const metricsConfig = useCallback((data) => {
    const summary = data.summary || {};
    const hasOffers = (summary.totalOffers || 0) > 0;

    return [
      {
        title: "Linguists Contacted",
        value: formatNumber(summary.totalOutreach),
        subtitle: `across ${summary.servicesWithOutreach || 0} services`,
        tooltipTitle: "Outreach Volume",
        tooltipContent:
          "Every outreach row for services dated in the selected period, including repeat contact with the same linguist.",
      },
      {
        title: "Fill Rate",
        value: formatPercentValue(summary.fillRate, { digits: 1 }),
        subtitle: `${summary.filledServices || 0} of ${
          summary.servicesWithOutreach || 0
        } staffed`,
        color:
          (summary.fillRate || 0) >= 80
            ? "success.main"
            : (summary.fillRate || 0) >= 50
              ? "warning.main"
              : "error.main",
        tooltipTitle: "Fill Rate",
        tooltipContent:
          "Share of services that received at least one outreach and still ended up with a linguist assigned. Low values mean the outreach list is not converting into coverage.",
      },
      {
        title: "Acceptance Rate",
        value: formatPercentValue(summary.acceptanceRate, { digits: 1 }),
        subtitle: "of answered requests",
        color: "success.main",
        tooltipTitle: "Acceptance Rate",
        tooltipContent:
          "Accepted responses divided by accepted + not available + declined. Silence (no response / not reached) is excluded so unreachable linguists do not masquerade as rejections.",
      },
      {
        title: "Response Rate",
        value: formatPercentValue(summary.responseRate, { digits: 1 }),
        subtitle: `${summary.responded || 0} answers recorded`,
        color:
          (summary.responseRate || 0) >= 50 ? "success.main" : "warning.main",
        tooltipTitle: "Response Rate",
        tooltipContent:
          "Any logged answer (accepted, not available, will confirm back, declined) over all outreach. A low rate points at contact-data quality or channel problems.",
      },
      {
        title: "Offers Accepted",
        value: hasOffers
          ? `${summary.offersAccepted} / ${summary.totalOffers}`
          : "No offers",
        subtitle: hasOffers
          ? `Avg response ${summary.avgOfferResponseHours ?? "-"}h`
          : "Offer workflow not used yet",
        color: hasOffers ? "primary.main" : "text.secondary",
        tooltipTitle: "Formal Offers",
        tooltipContent:
          "Formal assignment offers sent through the portal. Periods before the offer workflow existed report no offers even though outreach was logged.",
      },
    ];
  }, []);

  const chartsConfig = useCallback(
    (data) => {
      const statusBreakdown = data.statusBreakdown || [];
      const monthlyTrend = data.monthlyTrend || [];
      const linguistRows = data.linguistRows || [];

      if ((data.summary?.totalOutreach || 0) === 0) {
        return (
          <Box
            sx={{
              py: 6,
              textAlign: "center",
              bgcolor: theme.palette.background.alt,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.primary.border2}`,
            }}
          >
            <Typography variant="body1" fontWeight={600} gutterBottom>
              No outreach logged in this period
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Outreach is recorded from the Pre-Assign &amp; Outreach screen. Pick
              a period that includes recent staffing work.
            </Typography>
          </Box>
        );
      }

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 5 }}>
            <ReportPieChart
              title="Outreach Outcomes"
              data={statusBreakdown.map((row) => ({
                label: row.label,
                value: row.value,
                color: STATUS_COLORS[row.label]
                  ? theme.palette[STATUS_COLORS[row.label].split(".")[0]]?.main
                  : undefined,
              }))}
              height={360}
              tooltipTitle="Outreach Outcomes"
              tooltipContent="How linguists answered. 'Not reached' means no contact was ever recorded — those rows are worth cleaning up or retrying."
            />
          </Grid>

          <Grid size={{ xs: 12, lg: 7 }}>
            {monthlyTrend.length > 1 ? (
              <ReportLineChart
                title="Outreach Activity"
                xAxisData={monthlyTrend.map((row) => row.month)}
                series={[
                  {
                    data: monthlyTrend.map((row) => row.outreach),
                    label: "Contacted",
                    color: chartColors.semantic.primary,
                  },
                  {
                    data: monthlyTrend.map((row) => row.accepted),
                    label: "Accepted",
                    color: chartColors.semantic.positive,
                  },
                  {
                    data: monthlyTrend.map((row) => row.unavailable),
                    label: "Not available",
                    color: chartColors.semantic.warning,
                  },
                ]}
                valueFormatter={formatNumber}
                yAxisLabel="Count"
                height={360}
                tooltipTitle="Outreach Activity"
                tooltipContent="Monthly outreach volume against the answers received. Use it to spot months where volume rose but acceptance did not."
              />
            ) : (
              <ReportBarChart
                title="Outreach by Month"
                data={monthlyTrend}
                xAxisKey="month"
                series={[
                  {
                    dataKey: "outreach",
                    label: "Contacted",
                    color: chartColors.semantic.primary,
                  },
                  {
                    dataKey: "accepted",
                    label: "Accepted",
                    color: chartColors.semantic.positive,
                  },
                ]}
                valueFormatter={formatNumber}
                yAxisLabel="Count"
                height={360}
              />
            )}
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ReportTable
              title="Linguist Outreach Performance"
              subtitle="Ranked by number of times contacted in the period."
              columns={[
                { field: "linguistName", headerName: "Linguist", width: 260 },
                { field: "outreachCount", headerName: "Contacted", align: "right" },
                { field: "accepted", headerName: "Accepted", align: "right" },
                { field: "notAvailable", headerName: "Not Available", align: "right" },
                { field: "noResponse", headerName: "No Response", align: "right" },
                { field: "notReached", headerName: "Not Reached", align: "right" },
                {
                  field: "acceptanceRate",
                  headerName: "Acceptance",
                  align: "right",
                  renderCell: (row) =>
                    formatPercentValue(row.acceptanceRate, { digits: 1 }),
                },
              ]}
              rows={linguistRows}
              emptyMessage="No outreach recorded in this period"
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors, theme],
  );

  return (
    <DashboardReport
      title="Outreach & Fill Rate"
      description="Pre-assignment funnel for services in the selected period: who was contacted, how they answered, and how often the service ended up staffed."
      dataFetchFn={dataFetchFn}
      filters={filters}
      metricsConfig={metricsConfig}
      chartsConfig={chartsConfig}
    />
  );
};
